// =============================================================================
// components/LogoWordmark.tsx — the CRYB brand wordmark (design-system rule #6).
//
// Screens must render this instead of a plain <Text>CRYB</Text> so the logo is
// uniform everywhere (weight, tracking, gradient). Swap the internals here only —
// every call site (LogoWordmark) stays unchanged.
//
// The mark is the animated <AnimatedLogo mode="flow"> — CRYB letters filled with
// a purple→blue→white gradient that drifts through them forever. On colored
// surfaces (monochrome) it falls back to a solid <Text> so it stays legible.
// =============================================================================
import React from 'react';
import { View, Text, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import { colors, fonts, ThemeMode, colorTokens } from '../constants/theme';
import AnimatedLogo from './AnimatedLogo';

export interface LogoWordmarkProps {
  /** Cap height of the wordmark in px. Default 28. */
  size?: number;
  /** Theme mode. 'light' (default) or 'dark'. Affects the solid fallback tint. */
  theme?: ThemeMode;
  /** Solid tint instead of the brand gradient (e.g. on a colored surface). */
  monochrome?: boolean;
  /** Override color when monochrome. */
  color?: string;
  style?: StyleProp<ViewStyle>;
}

const WORD = 'CRYB';

export default function LogoWordmark({
  size = 28,
  theme = 'light',
  monochrome = false,
  color,
  style,
}: LogoWordmarkProps) {
  // On a colored surface, keep a legible solid wordmark (the drifting gradient
  // — which includes white — would wash out). Everywhere else: the flow mark.
  if (monochrome) {
    const fontSize = size;
    const height = fontSize * 1.2;
    const solid = color ?? colorTokens[theme].text;
    return (
      <View style={[styles.wrap, style]} accessibilityRole="header" accessibilityLabel="CRYB">
        <Text style={[styles.fallback, { fontSize, color: solid, lineHeight: height }]}>
          {WORD}
        </Text>
      </View>
    );
  }

  return <AnimatedLogo mode="flow" size={size} theme={theme} style={style} />;
}

const styles = StyleSheet.create({
  wrap: {
    alignSelf: 'flex-start',
  },
  fallback: {
    ...fonts.extrabold,
    letterSpacing: 2,
  },
});
